import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import api from "common/api";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { ITeacher, SubjectKind } from "common/types";
import DeleteInstructorConfirmation from "./DeleteInstructorConfirmation";
import { MdClear, MdVisibilityOff } from "react-icons/md";
import Spinner from "../../../spinner";
import usePageTitle from "common/usePageTitle";

interface TeacherAdminListPageProps {
  kind: SubjectKind;
}

function TeacherAdminListPage({ kind }: TeacherAdminListPageProps) {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState<boolean>(false);

  usePageTitle("Nastavnici admin");

  const getProfileKey = () =>
    kind === SubjectKind.LESSON ? "lesson_profile" : "profile";

  useEffect(() => {
    const fetchTeachers = async () => {
      setLoading(true);
      try {
        const response = await api.get("/teachers/");
        const filteredTeachers = response.data.filter((teacher: any) => {
          return kind === SubjectKind.LESSON
            ? teacher.lesson_profile != null
            : teacher.profile != null;
        });
        setTeachers(filteredTeachers);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, [kind]);

  const deleteInstructor = async (user_id: string) => {
    try {
      await api.delete(`/teachers/${user_id}/`);
      setTeachers(
        teachers.filter((teacher: ITeacher) => teacher.user_id !== user_id),
      );
      toast.success("Nastavnik obrisan");
    } catch (error) {
      console.log(error);
      toast.error("Greška prilikom brisanja nastavnika");
    }
  };

  const columns = [
    {
      name: "Ime",
      selector: (row: any) => row.first_name,
      sortable: true,
    },
    {
      name: "Prezime",
      selector: (row: any) => row.last_name,
      sortable: true,
    },
    {
      name: "Status",
      width: "80px",
      cell: (row: any) => {
        const isHidden = row[getProfileKey()]?.hidden;
        return isHidden ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-${row.user_id}`}>Sakriven profil</Tooltip>
            }
          >
            <div>
              <MdVisibilityOff size={20} color="#dc3545" />
            </div>
          </OverlayTrigger>
        ) : null;
      },
    },
    {
      name: "Predmeti",
      selector: (row: any) => row[getProfileKey()]?.subjects,
      grow: 2,
      cell: (row: any) => (
        <div>
          {row[getProfileKey()]?.subjects.map((subj: any) => (
            <span
              key={subj.id}
              style={{
                padding: "4px 8px",
                marginRight: "5px",
                backgroundColor: "#8d8d8d",
                color: "white",
                borderRadius: "5px",
              }}
            >
              {subj.name}
            </span>
          ))}
        </div>
      ),
    },
    {
      button: true,
      cell: (row: any) => {
        return (
          <>
            <DeleteInstructorConfirmation
              onDeleteConfirm={deleteInstructor}
              instructor={row}
            />
          </>
        );
      },
    },
  ];

  const handleRowClick = (row: { user_id: any }) => {
    if (kind === SubjectKind.LESSON) {
      navigate(`/poduke/nastavnici-admin/${row.user_id}`);
    } else if (kind === SubjectKind.PREPARATION)
      navigate(`/nastavnici-admin/${row.user_id}`);
  };

  const handleSearchChange = (event: any) => {
    setSearch(event.target.value);
  };

  const filteredTeachers: any[] = teachers.filter(
    (teacher: any) =>
      teacher.first_name.toLowerCase().includes(search.toLowerCase()) ||
      teacher.last_name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Nastavnici</h1>
        </Col>

        <Col xs="auto" xl={5}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Pretraži nastavnike..."
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <InputGroup.Text
                style={{ cursor: "pointer" }}
                onClick={() => setSearch("")}
                title="Obriši pretragu"
              >
                <MdClear />
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>

        <Col xs="auto" xl={5}>
          <Button
            variant="primary"
            onClick={() => {
              if (kind === SubjectKind.LESSON) {
                navigate("/poduke/dodaj-nastavnika/");
              } else if (kind === SubjectKind.PREPARATION) {
                navigate("/dodaj-nastavnika/");
              }
            }}
          >
            Dodaj nastavnika
          </Button>
        </Col>
      </Row>
      <DataTable
        // @ts-ignore
        columns={columns}
        data={filteredTeachers} // Use filtered data based on search
        onRowClicked={handleRowClick}
        pointerOnHover
        highlightOnHover
        pagination
        progressPending={loading}
        progressComponent={<Spinner />}
        noDataComponent={`Ne postoje nastavnici za ${
          kind === SubjectKind.LESSON ? "poduke" : "pripreme"
        }`}
      />
    </div>
  );
}

export default TeacherAdminListPage;
